import { useState, useEffect, VFC } from "react";
import { Grid, Box, Button, SelectChangeEvent, Typography } from "@mui/material";
import { AxiosResponse } from "axios";
import ButtonArea from "../common/ButtonArea";
import { FormText } from "../common/form/FormText";
import { ResultResponse, ResultDialog } from "../common/dialog/ResultDialog";
import { Loading } from "../common/Loading";
import { ProtectedApiAuth } from "../protected/ProtectedApiAuth";
import Constants from "../../utils/Constants";
import MdmUserDialogMode from "../../entity/MdmUserDialogMode";
import MdmUserDialog from "./MdmUserEditDialog";
import { MdmUserTable } from "../common/table/MdmUserTable";
import { MdmUserSearchRequest, MdmUserSearchResponse } from "./api/MdmUserForm";
import { MdmUserService } from "./api/MdmUserService";

/**
 * アカウント一覧画面
 * MDMユーザーを表示上は「アカウント」と表記する
 */
const MdmUserList: VFC = () => {
    const PAGE_NAME = "アカウント一覧";
    const WIDTH = 2;

    /***************************************************************
     * State管理
     **************************************************************/
    // 共通ローディング
    const [isLoading, setIsLoading] = useState(false);

    // 検索条件
    const [request, setRequest] = useState<MdmUserSearchRequest>(
        new MdmUserSearchRequest()
    );

    // ユーザー一覧
    const [mdmUserList, setMdmUserList] = useState([] as MdmUserSearchResponse[]);

    // ユーザー操作ダイアログ
    const [isMdmUserOpen, setIsMdmUserOpen] = useState(false);

    // ユーザー操作ダイアログ用編集ユーザーID
    const [mdmUserDialogId, setMdmUserDialogId] = useState("");

    // ダイアログのキー（ダイアログを新規作成するためのトリガー）
    const [dialogKey, setDialogKey] = useState(0);

    // テーブルのキー（検索によりアカウント一覧テーブルページをリセットするためのトリガー）
    const [tableKey, setTableKey] = useState(0);

    // 共通ダイアログ(ステータス)
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    // 処理結果
    const [resultResponse, setResultResponse] = useState<ResultResponse>({
        status: "",
        code: "",
        displayMessage: "",
        result: [],
        errorMessages: [],
    });

    /***************************************************************
     * 共通処理
     **************************************************************/
    // 処理結果ダイアログ(CLOSE)
    const handleResultDialogClose = () => {
        setIsDialogOpen(false);
    };

    // 処理結果を設定
    const setStateResultResponse = (
        status: string,
        code: string,
        displayMessage: string,
        result: string
    ) => {
        setIsDialogOpen(true);
        setResultResponse({
        status,
        code,
        displayMessage,
        result: [result],
        errorMessages: [],
        });
    };

    /***************************************************************
     * 初期表示処理
     **************************************************************/
    useEffect(() => {
        // セッション破棄
        sessionStorage.removeItem("TENANT_ID");

        // 検索処理
        searchButtonClick();
    }, []);

    /***************************************************************
     * 検索処理
     **************************************************************/
    // 入力変更
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setRequest({ ...request, [e.target.name]: e.target.value });
    };

    // 検索実行(Enter)
    const handleInputSearch = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Enter") {
            e.preventDefault();
            searchButtonClick();
        }
    };

    // 検索ボタン押下
    const searchButtonClick = () => {
        setIsLoading(true);
        // アカウント一覧テーブルをリセットするためにキーを更新
        setTableKey(prev => prev + 1);
        // ユーザー一覧取得API呼び出し
        const service = new MdmUserService();
        service
            .findByConditions(request)
            .then((response: AxiosResponse) => {
                if (Constants.ERR_STATUS_OK === response.data.status) {
                    setMdmUserList(response.data.data);
                } else {
                    setIsDialogOpen(true);
                    setResultResponse(response.data);
                }
        })
        .catch((err) => {
            setStateResultResponse(
                Constants.ERR_STATUS_NG,
                err.response.status,
                err.response.statusText,
                Constants.E_MSG_SYSTEM_ERR
            );
        })
        .finally(() => {
            setIsLoading(false);
        });
    };

    /***************************************************************
     * ユーザーの追加・編集
     **************************************************************/
    // ユーザー追加ダイアログ（OPEN）
    const createUserButtonClick = () => {
        // 画面遷移なしでポップアップ表示
        setMdmUserDialogId("");
        setDialogKey(prev => prev + 1); // 新しいダイアログインスタンスを作成
        setIsMdmUserOpen(true);
    }

    // ユーザー編集ダイアログ（OPEN）
    const editUserButtonClick = (mdmUserId: string) => {
        // 画面遷移なしでポップアップ表示
        setMdmUserDialogId(mdmUserId);
        setDialogKey(prev => prev + 1); // 新しいダイアログインスタンスを作成
        setIsMdmUserOpen(true);
    }

    // ユーザー追加・編集ダイアログ（CLOSE）
    const handleMdmUserClose = () => {
        setIsMdmUserOpen(false);
    }

    // ユーザー追加・編集ダイアログ（OK）
    const handleMdmUserOk = () => {
        searchButtonClick();
    }

    /***************************************************************
     * 権限処理
     **************************************************************/
    // 権限なしのオーバーライド
    const overrideFunction = (response: ResultResponse) => {
        setResultResponse(response);
        setIsDialogOpen(true);
    }

    /***************************************************************
     * レイアウト
     **************************************************************/
    // 検索フォーム
    const SearchForm = () => {
        return (
            <Box sx={{ p: 2, mt: -4 }}>
                <Grid container spacing={2} alignItems="center">
                    {/* 検索条件：テナント名 */}
                    <FormText
                        label="テナント名"
                        name="tenantName"
                        xs={WIDTH}
                        handle={handleInputChange}
                        handleKeyDown={handleInputSearch}
                    />

                    {/* 検索条件：氏名 */}
                    <FormText
                        label="氏名"
                        name="fullName"
                        xs={WIDTH}
                        handle={handleInputChange}
                        handleKeyDown={handleInputSearch}
                    />

                    {/* 検索条件：メールアドレス */}
                    <FormText
                        label="メールアドレス"
                        name="email"
                        xs={WIDTH}
                        handle={handleInputChange}
                        handleKeyDown={handleInputSearch}
                    />

                    {/* 検索ボタン */}
                    <Grid item xs={1}>
                        <Button 
                            variant="contained" onClick={searchButtonClick}>
                            検索
                        </Button>
                    </Grid>
                </Grid>
            </Box>
        );
    };

    return (
        <>
            <Box>
                {/* ボタンエリア */}
                <ButtonArea pageName={PAGE_NAME}>
                    <ProtectedApiAuth
                        authcd={Constants.AUTH_MDM_USER_CREATE}
                        defaultFunction={() => createUserButtonClick()}
                        overrideFunction={overrideFunction}
                    >
                        <Button
                            variant="contained"
                            color="primary"
                            sx={{ textTransform: "none" }}
                        >
                            LiNKアカウント追加
                        </Button>
                    </ProtectedApiAuth>
                </ButtonArea>

                {/* メインエリア */}
                <Box sx={{ pt: 4 }}>
                    {/* 検索フォーム */}
                    {SearchForm()}
                    {/* アカウント一覧 */}
                    {mdmUserList.length > 0 ? <>
                        <MdmUserTable
                            mdmUserList={mdmUserList}
                            onEditButtonClick={editUserButtonClick}
                            resetPage={tableKey}
                        />
                    </> : <>
                        <Box>
                            <Grid container alignItems={'center'} justifyContent={'center'}>
                                {/* アカウント情報なし */}
                                <Grid item xs={12}  padding={3}>
                                    <Typography variant="h2" align="center" fontSize={"1.25rem"} noWrap>
                                        アカウント情報なし
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    </>}
                </Box>
            </Box>

            {/* MDMユーザーダイアログ */}
            {isMdmUserOpen && (
                <MdmUserDialog
                    key={dialogKey}
                    isAlertDialogOpen={isMdmUserOpen}
                    mode="保存"
                    mdmUserId={mdmUserDialogId}
                    defaultDialogMode={mdmUserDialogId !== "" ? MdmUserDialogMode.EDIT : MdmUserDialogMode.CREATE}
                    handleAlertCancel={handleMdmUserClose}
                    handleAlertOk={handleMdmUserOk}
                />
            )}

            {/* 処理結果ダイアログ */}
            <ResultDialog
                isDialogOpen={isDialogOpen}
                resultResponse={resultResponse}
                handler={handleResultDialogClose}
            />

            {/* 処理ローディング */}
            <Loading isLoading={isLoading} />
        </>
    );
};

export default MdmUserList;
